import { useMarkets } from '@/state/markets';
import { useWallet } from '@/state/wallet';
import { useLife } from '@/state/life';
import { useMeta } from '@/state/meta';
import { usePhone } from '@/state/phone';
import { useXClone } from '@/state/xclone';
import { useEmail } from '@/state/email';
import { useCinematic } from '@/components/CinematicTakeover';
import { sounds } from '@/audio/synth';
import { saveNow } from './save';

/**
 * "True death" per META.md — vanishingly rare scripted ending when player has
 * exhausted every Desperation move. Different from voluntary ascension:
 *   - NO Carry Coins awarded
 *   - Graveyard entry tagged "irrecoverable"
 *   - Unique cinematic
 *   - Forced restart after the cinematic
 *
 * Condition (very strict): cash deeply negative, no liquid possessions,
 * sanity collapsed, multiple heat stats maxed.
 */

let firedThisRun = false;

export function checkTrueDeath(): void {
  if (firedThisRun) return;
  if (useCinematic.getState().current?.id === 'true-death') return;

  const m = useMarkets.getState();
  const w = useWallet.getState();
  const l = useLife.getState();

  if (m.cash > -10_000) return;
  if (l.sanity > 5) return;
  const possessionsLeft = w.possessions.filter((p) => p.available).length;
  if (possessionsLeft > 0) return;
  // Need at least two heat stats >= 70
  const highHeat = [l.irsHeat, l.secHeat, l.fbiHeat].filter((h) => h >= 70).length;
  if (highHeat < 2) return;
  // Need at least 2 relationships at the floor
  const dead = Object.values(l.relationships).filter((r) => r <= -90).length;
  if (dead < 2) return;

  firedThisRun = true;
  triggerTrueDeath();
}

function triggerTrueDeath(): void {
  sounds.marginCall();
  useCinematic.getState().fire({
    id: 'true-death',
    type: 'true-death',
    headline: 'YOU HAVE EXHAUSTED EVERY MOVE.',
    subhead: 'no cash. no assets. no allies. no leverage. the IRS will collect what is left of your name. you do not even get the cinematic credits.',
    durationMs: 8000,
  });

  // After the cinematic, hard-reset per-run state without awarding Carry Coins.
  setTimeout(() => {
    const meta = useMeta.getState();
    meta.recordAscension({
      characterId: `char-${meta.ascensions + 1}`,
      ascendedAt: Date.now(),
      highestTierRank: meta.highestTierRankReached,
      causeOfDeath: 'irrecoverable — true death',
      finalNetWorth: useMarkets.getState().cash,
      runDurationMs: 0,
    });
    // No coins awarded.

    useMarkets.setState({
      cash: 5_000,
      positions: [],
      openOrders: [],
      realizedPnl: 0,
      recentFills: [],
      activeTickerId: 'TARI',
      equityATH: 0,
    });
    useLife.setState({
      relationships: { wife: 30, mom: 60, broker: 0, lawyer: 0, best_friend: 50 },
      health: 80,
      sanity: 60,
      irsHeat: 0,
      secHeat: 0,
      fbiHeat: 0,
      apartmentTier: 'studio',
      insuranceStatus: 'normal',
      creditScore: 680,
      lawyerRetainerActiveSince: null,
      lastDecayMs: Date.now(),
    });
    useWallet.setState({
      possessions: [
        { id: 'dog', label: 'The dog', description: '12 lbs of unconditional love. Sells well, ruins your life.', sellPrice: 300, available: true },
        { id: 'watch', label: 'Bonus Rolex', description: 'Heirloom. Pawnable. He will know.', sellPrice: 1500, available: true },
        { id: 'car', label: 'The car', description: '2019 sedan. Smells like vape. Liquid for the price.', sellPrice: 5000, available: true },
        { id: 'house', label: 'The house', description: '"Starter home." Mortgage included free of charge.', sellPrice: 50000, available: true },
      ],
      ownsCharityFoundation: false,
      ownsYacht: false,
      ownsJet: false,
      ownsIsland: false,
      ownsSportsTeam: false,
      ownsNewspaper: false,
    });
    usePhone.setState({
      contacts: usePhone.getState().contacts,
      messages: [],
      activeThreadId: null,
    });
    useXClone.setState({
      posts: [],
      followers: 14,
    });
    useEmail.setState({
      emails: [],
      selectedId: null,
    });
    saveNow();
    firedThisRun = false;
  }, 8500);
}
